import { useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { TenantSwitcher } from '@/components/TenantSwitcher';
import { UserMenu } from '@/components/UserMenu';
import { cn } from '@/components/ui/cn';
import { useWorkspacePath } from '@/lib/workspacePath';

interface NavItem {
  /** Workspace-relative segment(s); '' is the workspace dashboard (index). */
  to: string;
  label: string;
  end?: boolean;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

type NavEntry = NavItem | NavGroup;

function isNavGroup(entry: NavEntry): entry is NavGroup {
  return 'items' in entry;
}

// "Products" always renders expanded: it is the first group of what will
// become several agent-resource areas (inventory joins it next). Paths are
// workspace-relative (spec 0013); the active `:workspaceSlug` is prefixed at
// render time.
const navItems: NavEntry[] = [
  { to: '', label: 'Dashboard', end: true },
  { to: 'agents', label: 'Agents' },
  { label: 'Products', items: [{ to: 'catalogs', label: 'Catalogs' }] },
  { to: 'connectors', label: 'Connectors' },
  { to: 'knowledge', label: 'Knowledge' },
  { to: 'settings/team', label: 'Team' },
  { to: 'settings/billing', label: 'Billing' },
];

function MenuIcon() {
  return (
    <svg
      className="h-6 w-6"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      className="h-6 w-6"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
    </svg>
  );
}

function NavLinkItem({
  to,
  label,
  end,
  onNavigate,
}: {
  to: string;
  label: string;
  end?: boolean;
  onNavigate: () => void;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'flex min-h-[2.75rem] items-center rounded-md px-3 text-sm font-medium transition-colors motion-reduce:transition-none lg:min-h-0 lg:py-2',
          isActive
            ? 'bg-brand-50 text-brand-700'
            : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900',
        )
      }
    >
      {label}
    </NavLink>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();
  const ws = useWorkspacePath();
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const wasOpenRef = useRef(false);

  // Close the mobile drawer whenever the route changes.
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  // While the drawer is open: close on Escape and move focus into it. On an
  // open -> close transition, return focus to the menu button that opened it
  // (but never steal focus on the initial render).
  useEffect(() => {
    if (!navOpen) {
      if (wasOpenRef.current) menuButtonRef.current?.focus();
      wasOpenRef.current = false;
      return;
    }
    wasOpenRef.current = true;
    closeButtonRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setNavOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navOpen]);

  return (
    <div className="flex h-full">
      {navOpen && (
        <div
          className="fixed inset-0 z-30 bg-gray-900/40 lg:hidden"
          aria-hidden="true"
          onClick={() => setNavOpen(false)}
        />
      )}

      <aside
        id="app-sidebar"
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex w-60 shrink-0 flex-col border-r border-gray-200 bg-white',
          'transition-transform duration-200 motion-reduce:transition-none',
          'lg:static lg:z-auto lg:translate-x-0',
          navOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex h-14 items-center justify-between pl-5 pr-3">
          <span className="text-lg font-semibold tracking-tight text-gray-900">saasd</span>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={() => setNavOpen(false)}
            aria-label="Close navigation"
            className="inline-flex h-11 w-11 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 lg:hidden"
          >
            <CloseIcon />
          </button>
        </div>
        <nav className="flex-1 space-y-1 px-3 py-2">
          {navItems.map((entry) =>
            isNavGroup(entry) ? (
              <div key={entry.label} className="space-y-1 pt-2 first:pt-0">
                <p className="px-3 text-xs font-semibold uppercase tracking-wide text-gray-400">
                  {entry.label}
                </p>
                {entry.items.map((item) => (
                  <NavLinkItem
                    key={item.to}
                    to={ws(item.to)}
                    label={item.label}
                    end={item.end}
                    onNavigate={() => setNavOpen(false)}
                  />
                ))}
              </div>
            ) : (
              <NavLinkItem
                key={entry.to}
                to={ws(entry.to)}
                label={entry.label}
                end={entry.end}
                onNavigate={() => setNavOpen(false)}
              />
            ),
          )}
        </nav>
        <div className="border-t border-gray-200 px-5 py-3 text-xs text-gray-500">
          AI agents platform
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 shrink-0 items-center gap-2 border-b border-gray-200 bg-white px-4 sm:px-6">
          <button
            ref={menuButtonRef}
            type="button"
            onClick={() => setNavOpen(true)}
            aria-label="Open navigation"
            aria-controls="app-sidebar"
            aria-expanded={navOpen}
            className="-ml-1 inline-flex h-11 w-11 items-center justify-center rounded-md text-gray-700 hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 lg:hidden"
          >
            <MenuIcon />
          </button>
          <div className="flex min-w-0 flex-1 items-center justify-between gap-3">
            <TenantSwitcher />
            <UserMenu />
          </div>
        </header>
        <main className="min-h-0 flex-1 overflow-auto p-4 sm:p-6">{children}</main>
      </div>
    </div>
  );
}
